/*
 * CAPTURE-OWNED MODULE
 * The one sentence a reviewer reads under a flagged field's value.
 *
 * capture_extracted_fields.evidence has been written by the extraction seam
 * since 2026-09-08 and read by nobody: a flagged field showed the same row as
 * a clean one, with no mark and no explanation. This turns one evidence blob
 * into one plain-English line.
 *
 * THREE RULES GOVERN EVERY STRING BELOW (Bret, 2026-09-10):
 *
 *  1. Keyed on `evidence.source`, NEVER on the presence of `evidence.arithmetic`.
 *     Ten of the ten live `format_repaired` rows also carry an arithmetic block,
 *     and every one of those blocks says `confirmed` — keying on the block would
 *     announce an arithmetic problem on a document that adds up to the cent.
 *  2. Nothing internal reaches the reviewer: no source names, no field keys, no
 *     profile UUIDs, no cents. `pattern_miss_no_fallback` stores a prose
 *     `reason` with a UUID inside it; that string is never rendered.
 *     Term labels come from the Field Library via `siblingLabels`.
 *  3. Only `needs_review` produces a reason. A `suggested` row carrying a
 *     confirmed arithmetic block is not flagged and gets nothing.
 *
 * When the pieces a sentence needs are missing from the blob, every branch
 * falls back to 'Needs review' rather than composing half a sentence — the
 * shapes here are what production writes today, not a contract the writers
 * have promised to keep.
 */

/** The row is flagged and we cannot say anything more specific than that. */
const NEEDS_REVIEW = 'Needs review';

/** capture_extracted_fields.source for a value the targeted fill supplied. */
const AI_TEXT_SOURCE = 'claude_text';

/* Terms are read off the blob, never assumed: `terms` has held two keys
 * (total + subtotal, tax missing entirely) and three (with tax). These four are
 * ordered for reading; anything else follows, alphabetically, so the sentence
 * is stable across runs whatever the seam starts recording. */
const TERM_READING_ORDER = ['subtotal', 'tax', 'shipping', 'discount'];

function isPlainObject(value) {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value) {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * The ONE place cents become dollars. Everything else in this file passes
 * through raw strings the extractor already captured from the page, so the
 * reviewer reads the document's own numerals rather than a re-rendering of
 * them.
 */
function formatCents(cents) {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return null;
  const dollars = Math.abs(cents) / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Human label for a field key, lower-cased for mid-sentence use. */
function labelFor(key, siblingLabels) {
  const supplied = isPlainObject(siblingLabels) ? siblingLabels[key] : null;
  const label = isNonEmptyString(supplied)
    ? supplied
    : String(key || '').replace(/_/g, ' ');
  return label.toLowerCase();
}

/** "a" · "a and b" · "a, b and c" */
function joinPhrases(parts) {
  if (parts.length <= 1) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

function orderedTermKeys(terms) {
  const keys = Object.keys(terms).filter((k) => k !== 'total');
  return keys.sort((a, b) => {
    const ia = TERM_READING_ORDER.indexOf(a);
    const ib = TERM_READING_ORDER.indexOf(b);
    if (ia !== ib) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    return a.localeCompare(b);
  });
}

function formatFailureReason(evidence) {
  if (!isNonEmptyString(evidence.format) || !isNonEmptyString(evidence.raw)) return NEEDS_REVIEW;
  return `Doesn't match the expected format ${evidence.format} (read as "${evidence.raw}")`;
}

function arithmeticFailureReason(evidence, { valueText, fieldLabel, siblingLabels }) {
  const a = evidence.arithmetic;
  if (!isPlainObject(a)) return NEEDS_REVIEW;
  const terms = isPlainObject(a.terms) ? a.terms : {};
  const flagged = isNonEmptyString(evidence.flagged_field) ? evidence.flagged_field : null;

  // The total is the flagged number: say what the other amounts on the
  // document come to and how much of the total they leave unaccounted for.
  if (flagged === 'total') {
    const residual = formatCents(isPlainObject(a.computed) ? a.computed.residual_cents : null);
    if (!residual) return NEEDS_REVIEW;
    const others = orderedTermKeys(terms).filter((k) => isNonEmptyString(terms[k]?.raw));
    if (others.length === 0) return NEEDS_REVIEW;
    const phrase = joinPhrases(others.map((k) => `${labelFor(k, siblingLabels)} ${terms[k].raw}`));
    // The subject of the verb is the list of amounts, so it agrees with how
    // many were listed: one amount leaves, two or more leave. The blob decides
    // that at runtime — the same document type produces both.
    const verb = others.length > 1 ? 'leave' : 'leaves';
    // A missing tax line IS the explanation, not a detail — say so rather than
    // leaving the reviewer to work out why the sum is short.
    const noTax = terms.tax === undefined ? ' — no tax line was found' : '';
    return `Doesn't add up: ${phrase} ${verb} ${residual} unexplained${noTax}`;
  }

  // A component is the flagged number: hold it against the total, which is the
  // comparison a reviewer makes by eye anyway.
  const totalRaw = isNonEmptyString(terms.total?.raw) ? terms.total.raw : null;
  if (!flagged || !totalRaw || !isNonEmptyString(valueText)) return NEEDS_REVIEW;
  const label = isNonEmptyString(fieldLabel)
    ? fieldLabel.toLowerCase()
    : labelFor(flagged, siblingLabels);
  return `${valueText} can't be the ${label} of a ${totalRaw} total`;
}

function repairedReason(evidence) {
  if (!isNonEmptyString(evidence.raw)) return NEEDS_REVIEW;
  // Rule 1 applies in reverse here: the arithmetic block is not what flags the
  // row, but where it says `confirmed` it is the strongest reassurance the
  // reviewer can be given — the repaired figure balances the invoice.
  const confirmed = isPlainObject(evidence.arithmetic) && evidence.arithmetic.status === 'confirmed';
  return `Repaired from "${evidence.raw}"${
    confirmed ? " — confirmed by the invoice's own arithmetic" : ' — please confirm'
  }`;
}

/**
 * A value the targeted fill read from the page text (D2). It is flagged
 * because nobody has looked at it yet, not because anything is wrong with it,
 * so the sentence says where it was read and asks for a look. The label is the
 * document's own printed text, which is the one thing here a reviewer can
 * check against the page.
 */
function aiReadReason(evidence) {
  const label = isNonEmptyString(evidence.anchor) ? evidence.anchor.trim() : null;
  return label ? `Read by AI next to "${label}" - please confirm` : 'Read by AI - please confirm';
}

/** Longest neighbor text quoted in a truncation reason. */
const NEIGHBOR_TEXT_MAX = 40;

/**
 * Shorten the quoted neighbor text: whitespace collapsed, and anything over
 * NEIGHBOR_TEXT_MAX characters cut at the last word break that keeps at least
 * half of it (or mid-word when there is none), followed by "...".
 */
function shortNeighbor(text) {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= NEIGHBOR_TEXT_MAX) return clean;
  let cut = clean.slice(0, NEIGHBOR_TEXT_MAX);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace >= NEIGHBOR_TEXT_MAX / 2) cut = cut.slice(0, lastSpace);
  return `${cut.trimEnd()}...`;
}

/**
 * The template zone probably cut the value short (capture-app
 * services/extraction/truncationCheck). `evidence.truncation.side` says which
 * edge, and `neighbor_text` is the document's own text just past it, which is
 * what the reviewer looks for on the page.
 */
function truncationReason(evidence) {
  const t = evidence.truncation;
  if (!isPlainObject(t) || !isNonEmptyString(t.neighbor_text)) return NEEDS_REVIEW;
  const neighbor = shortNeighbor(t.neighbor_text);
  switch (t.side) {
    case 'right':
      return `The value may continue past the right edge of the template zone. The word "${neighbor}" follows it on the same line.`;
    case 'bottom':
      return `The value may continue below the template zone. The next line begins "${neighbor}".`;
    case 'straddle':
      return `Text overlaps the edge of the template zone but was not captured. The text "${neighbor}" sits partly inside the zone.`;
    default:
      return NEEDS_REVIEW;
  }
}

function notFoundReason(evidence) {
  const vendor = isNonEmptyString(evidence.vendor_name) ? evidence.vendor_name : null;
  return `Not found on this document${vendor ? ` (expected from the ${vendor} profile)` : ''}`;
}

/**
 * @param {object}  args
 * @param {string}  args.status         capture_extracted_fields.status
 * @param {object}  args.evidence       capture_extracted_fields.evidence
 * @param {string}  args.valueText      the value as displayed on the row
 * @param {string}  args.fieldLabel     this field's Field Library label
 * @param {object}  args.siblingLabels  { field_key: label } for the other fields
 * @param {string}  args.source         capture_extracted_fields.source
 * @returns {string|null} one sentence, or null when the row is not flagged
 */
export function fieldReason({ status, evidence, valueText, fieldLabel, siblingLabels, source } = {}) {
  if (status !== 'needs_review') return null;
  if (!isPlainObject(evidence)) return source === AI_TEXT_SOURCE ? aiReadReason({}) : NEEDS_REVIEW;

  switch (evidence.source) {
    case 'verification_failed_format':
      return formatFailureReason(evidence);
    case 'verification_failed_arithmetic':
      return arithmeticFailureReason(evidence, { valueText, fieldLabel, siblingLabels });
    case 'format_repaired':
      return repairedReason(evidence);
    case 'pattern_miss_no_fallback':
      return notFoundReason(evidence);
    case 'verification_failed_truncation':
      return truncationReason(evidence);
    case AI_TEXT_SOURCE:
      return aiReadReason(evidence);
    default:
      // A format or verification finding overwrites evidence.source, and the
      // cases above then say something more specific. Anything else on a row
      // the fill wrote still gets the fill's sentence, keyed on the ROW source.
      return source === AI_TEXT_SOURCE ? aiReadReason(evidence) : NEEDS_REVIEW;
  }
}

export default fieldReason;
